import React, { useState, useEffect } from "react";

const AlexaLifterPro = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Estado inicial
  const [competitors, setCompetitors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [readOnlyMode, setReadOnlyMode] = useState(
    localStorage.getItem("readOnlyMode") === "true"
  );
  const [activeTab, setActiveTab] = useState(
    localStorage.getItem("activeTab") || "snatch"
  );

  const changeTab = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem("activeTab", tabId);
  };

  const [currentSort, setCurrentSort] = useState(
    JSON.parse(localStorage.getItem("currentSort")) || {
      type: "snatch",
      criteria: "best",
    }
  );

  const [nextId, setNextId] = useState(4);
  const [toast, setToast] = useState({ show: false, message: "" });
  const [formData, setFormData] = useState({ name: "", weight: "" });

  // API_URL para desarrollo local
  const API_URL = "https://api.alexalifter.designwebirg.com/api";

  // Funciones de utilidad
  const getBest = (attempts = []) => {
    if (!Array.isArray(attempts)) return 0; // si no es array, devolvemos 0
    const valid = attempts.filter((a) => a > 0);
    return valid.length > 0 ? Math.max(...valid) : 0;
  };

  const getTotal = (competitor) => {
    return getBest(competitor.snatch) + getBest(competitor.cleanJerk);
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // 🔥 Nueva función para guardar en localStorage
  const saveToLocalStorage = (competitorsData = competitors) => {
    const appData = {
      competitors: competitorsData,
      config: {
        readOnly: readOnlyMode,
        sortBy: currentSort,
        version: "4.0",
        lastUpdated: new Date().toISOString(),
      },
    };
    localStorage.setItem("alexaLifterData", JSON.stringify(appData));
  };

  // 🔥 Nueva función para cargar desde localStorage
  const loadFromLocalStorage = () => {
    try {
      const savedData = localStorage.getItem("alexaLifterData");
      if (savedData) {
        const data = JSON.parse(savedData);
        setCompetitors(
          (data.competitors || []).map((c) => ({
            ...c,
            snatch: Array.isArray(c.snatch) ? c.snatch : [0, 0, 0],
            cleanJerk: Array.isArray(c.clean_jerk) ? c.clean_jerk : Array.isArray(c.cleanJerk) ? c.cleanJerk : [0, 0, 0],
          }))
        );
        if (data.config) {
          setReadOnlyMode(data.config.readOnly || false);
          setCurrentSort(data.config.sortBy || { type: "snatch", criteria: "best" });
        }
      }
    } catch (error) {
      console.error("Error cargando datos locales:", error);
    }
  };

  // Funciones de API
  const saveToAPI = async (data) => {
    try {
      await fetch(`${API_URL}/competitors`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      showToast("Datos Guardados correctamente");
    } catch (error) {
      console.log("API no disponible, usando almacenamiento local");
    }
  };

  const loadFromAPI = async () => {
    try {
      const response = await fetch(`${API_URL}/competitors`);
      if (response.ok) {
        const data = await response.json();
        const processedCompetitors = (data.competitors || []).map((c) => ({
          ...c,
          snatch: Array.isArray(c.snatch) ? c.snatch : [0, 0, 0],
          cleanJerk: Array.isArray(c.clean_jerk) ? c.clean_jerk : Array.isArray(c.cleanJerk) ? c.cleanJerk : [0, 0, 0],
        }));
        
        setCompetitors(processedCompetitors);
        setReadOnlyMode(data.config?.read_only || false);
        setCurrentSort(
          data.config?.sort_by || { type: "snatch", criteria: "best" }
        );
        
        // 🔥 Guardar también en localStorage
        saveToLocalStorage(processedCompetitors);
        showToast("Datos cargados desde API");
      }
    } catch (error) {
      console.log("API no disponible, usando datos locales");
      loadFromLocalStorage();
    }
  };

  // Cargar datos al iniciar
  useEffect(() => {
    loadFromAPI();
  }, []);

  // 🔥 Guardar en localStorage cuando cambian los competitors
  useEffect(() => {
    if (competitors.length > 0) {
      saveToLocalStorage();
    }
  }, [competitors]);

  // 🔥 Persistir currentSort en localStorage y reordenar
  useEffect(() => {
    localStorage.setItem("currentSort", JSON.stringify(currentSort));
    // Reordenar automáticamente cuando cambia el sorting
    if (competitors.length > 0) {
      const sorted = getSortedCompetitors(currentSort.type, currentSort.criteria);
      setCompetitors(sorted);
    }
  }, [currentSort]);

  useEffect(() => {
    localStorage.setItem("readOnlyMode", readOnlyMode);
  }, [readOnlyMode]);

  // 🔥 Nueva función para auto-guardar cambios
  const autoSaveChanges = async (newCompetitors = competitors) => {
    // Guardar en localStorage inmediatamente
    saveToLocalStorage(newCompetitors);
    
    // Intentar guardar en API
    try {
      const appData = {
        competitors: newCompetitors,
        config: {
          readOnly: readOnlyMode,
          sortBy: currentSort,
          version: "4.0",
          lastUpdated: new Date().toISOString(),
        },
      };
      await saveToAPI(appData);
    } catch (error) {
      console.log("Error guardando en API, datos guardados localmente");
    }
  };

  // Guardar en API solo cuando sea necesario (no automáticamente)
  const saveCurrentDataToAPI = async () => {
    const appData = {
      competitors,
      config: {
        readOnly: readOnlyMode,
        sortBy: currentSort,
        version: "4.0",
        lastUpdated: new Date().toISOString(),
      },
    };
    await saveToAPI(appData);
  };

  // Manejar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (readOnlyMode) {
      showToast("No puedes agregar en modo lectura", "error");
      return;
    }

    const name = formData.name.trim();
    const weight = parseFloat(formData.weight);

    if (!name || !weight || weight <= 0) {
      showToast("Por favor completa todos los campos correctamente", "error");
      return;
    }

    if (competitors.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      showToast("Ya existe un competidor con ese nombre", "error");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/competitors/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          weight,
          snatch: [0, 0, 0],
          cleanJerk: [0, 0, 0],
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newCompetitors = [...competitors, result.competitor];
        setCompetitors(newCompetitors);
        setFormData({ name: "", weight: "" });
        setNextId(result.competitor.id + 1);
        
        // 🔥 Auto-guardar después de agregar
        autoSaveChanges(newCompetitors);
        showToast("Competidor agregado");
      } else {
        showToast("Error al agregar competidor", "error");
      }
    } catch (error) {
      // 🔥 Si falla la API, agregar localmente
      const newCompetitor = {
        id: nextId,
        name,
        weight,
        snatch: [0, 0, 0],
        cleanJerk: [0, 0, 0],
      };
      const newCompetitors = [...competitors, newCompetitor];
      setCompetitors(newCompetitors);
      setFormData({ name: "", weight: "" });
      setNextId(nextId + 1);
      
      // 🔥 Auto-guardar después de agregar
      autoSaveChanges(newCompetitors);
      showToast("Competidor agregado (guardado localmente)", "error");
    }
  };

  // Ordenar competidores
  const getSortedCompetitors = (type, criteria, list = competitors) => {
    let sorted = [...list];
    if (searchQuery) {
      sorted = sorted.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch (criteria) {
      case "best":
        if (type === "snatch") {
          sorted.sort((a, b) => getBest(b.snatch) - getBest(a.snatch)); // Descendente
        } else if (type === "clean-jerk") {
          sorted.sort((a, b) => getBest(b.cleanJerk) - getBest(a.cleanJerk));
        }
        break;

      case "weight":
        if (type === "snatch") {
          sorted.sort((a, b) => (a.snatch[0] || 0) - (b.snatch[0] || 0));
        } else if (type === "clean-jerk") {
          sorted.sort((a, b) => (a.cleanJerk[0] || 0) - (b.cleanJerk[0] || 0));
        } else {
          sorted.sort((a, b) => a.weight - b.weight);
        }
        break;

      case "name":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;

      case "total":
        sorted.sort((a, b) => getTotal(b) - getTotal(a)); // Descendente
        break;
    }

    return sorted;
  };

  // 🔥 Actualizar competidor con auto-guardado
  const updateCompetitor = (id, field, value) => {
    if (readOnlyMode) return;

    setCompetitors((prev) => {
      const updated = prev.map((comp) => {
        if (comp.id === id) {
          if (field === "weight") {
            const weight = parseFloat(value);
            return weight > 0 ? { ...comp, weight } : comp;
          } else if (field === "name") {
            const name = value.trim();
            if (
              name &&
              !prev.some(
                (c) =>
                  c.id !== id && c.name.toLowerCase() === name.toLowerCase()
              )
            ) {
              return { ...comp, name };
            }
          }
        }
        return comp;
      });
      
      // 🔥 Auto-guardar después de actualizar
      setTimeout(() => autoSaveChanges(updated), 100);
      return updated;
    });
  };

  // Validar que los intentos sean progresivos
  const validateAttempts = (attempts) => {
    for (let i = 1; i < attempts.length; i++) {
      if (attempts[i] > 0 && attempts[i] < attempts[i - 1]) {
        return false;
      }
    }
    return true;
  };

  // 🔥 Actualizar intento con validación progresiva y auto-guardado
  const updateAttempt = (id, field, attemptIndex, value) => {
    if (readOnlyMode) return;

    // Normalizamos para evitar 0 a la izquierda
    let newValue = value ? parseInt(value, 10) : 0;

    // Si no es válido o negativo → se fuerza a 0
    if (isNaN(newValue) || newValue < 0) newValue = 0;

    // Si pasa el límite de 500 → bloquear
    if (newValue > 500) {
      showToast("⚠️ El peso máximo permitido es 500 kg", "error");
      newValue = 500;
    }

    setCompetitors((prev) => {
      const updated = prev.map((comp) => {
        if (comp.id === id) {
          const newAttempts = Array.isArray(comp[field])
            ? [...comp[field]]
            : [0, 0, 0];
          newAttempts[attemptIndex] = newValue;

          // 👉 Caso especial: si es el primer intento
          if (attemptIndex === 0) {
            // Si el primer intento es mayor que otros, los forzamos a 0
            for (let i = 1; i < newAttempts.length; i++) {
              if (newAttempts[i] > 0 && newAttempts[i] < newValue) {
                newAttempts[i] = 0;
              }
            }
          } else {
            // Validación progresiva para 2° y 3°
            if (!validateAttempts(newAttempts)) {
              showToast(
                `⚠️ ${field === "snatch" ? "Arranque" : "Envión"} ${
                  attemptIndex + 1
                } debe ser mayor o igual al anterior`,
                "error"
              );
              return comp; // 👈 no actualizamos si no cumple
            }
          }

          return { ...comp, [field]: newAttempts };
        }
        return comp;
      });
      
      // 🔥 Auto-guardar después de actualizar intento
      setTimeout(() => autoSaveChanges(updated), 100);
      return updated;
    });
  };

  // Eliminar competidor
  const deleteCompetitor = async (id) => {
    if (readOnlyMode) return;
    const pass = window.prompt("Ingrese la contraseña para eliminar:");
    if (pass !== "1307") {
      showToast("❌ Clave incorrecta. No se eliminó el competidor", "error");
      return;
    }
    if (window.confirm("¿Estás seguro de eliminar este competidor?")) {
      try {
        const response = await fetch(`${API_URL}/competitors/${id}`, {
          method: "DELETE",
        });

        if (response.ok) {
          const result = await response.json();
          const newCompetitors = competitors.filter((c) => c.id !== id);
          setCompetitors(newCompetitors);
          
          // 🔥 Auto-guardar después de eliminar
          autoSaveChanges(newCompetitors);
          showToast(`Competidor ${result.competitor.name} eliminado`);
        } else {
          const error = await response.json();
          showToast(`Error: ${error.error}`, "error");
        }
      } catch (err) {
        // 🔥 Si falla la API, eliminar localmente
        const newCompetitors = competitors.filter((c) => c.id !== id);
        setCompetitors(newCompetitors);
        
        // 🔥 Auto-guardar después de eliminar
        autoSaveChanges(newCompetitors);
        showToast("Competidor eliminado (guardado localmente)", "error");
      }
    }
  };

  // Limpiar todo
  const clearAll = async () => {
    if (readOnlyMode) {
      showToast("No puedes limpiar en modo lectura");
      return;
    }
    const pass = window.prompt("Ingrese la contraseña para eliminar todos:");
    if (pass !== "1307") {
      showToast("❌ Clave incorrecta. No se eliminó nada", "error");
      return;
    }
    if (window.confirm("¿Estás seguro de eliminar todos los datos?")) {
      try {
        const response = await fetch(`${API_URL}/competitors`, {
          method: "DELETE",
        });

        if (response.ok) {
          setCompetitors([]);
          setCurrentSort({ type: "snatch", criteria: "best" });
          setNextId(1);
          
          // 🔥 Limpiar localStorage también
          localStorage.removeItem("alexaLifterData");
          showToast("Todos los competidores eliminados");
        } else {
          const error = await response.json();
          showToast(`Error: ${error.error}`, "error");
        }
      } catch (err) {
        // 🔥 Si falla la API, limpiar localmente
        setCompetitors([]);
        setCurrentSort({ type: "snatch", criteria: "best" });
        setNextId(1);
        
        // 🔥 Limpiar localStorage también
        localStorage.removeItem("alexaLifterData");
        showToast("Todos los competidores eliminados (guardado localmente)", "error");
      }
    }
  };

  // 🔥 Cambiar modo con auto-guardado
  const toggleReadOnlyMode = async () => {
    const newMode = !readOnlyMode;
    setReadOnlyMode(newMode);

    const appData = {
      competitors,
      config: {
        readOnly: newMode, // 👈 usamos el nuevo valor
        sortBy: currentSort,
        version: "4.0",
        lastUpdated: new Date().toISOString(),
      },
    };

    // 🔥 Guardar en localStorage inmediatamente
    localStorage.setItem("alexaLifterData", JSON.stringify(appData));

    try {
      await saveToAPI(appData);
      showToast(`Modo ${newMode ? "Lectura" : "Edición"} activado`);
    } catch (err) {
      showToast(`Modo ${newMode ? "Lectura" : "Edición"} activado (guardado localmente)`, "error");
    }
  };

  // 🔥 Función para cambiar ordenamiento con persistencia
  const changeSorting = (type, criteria) => {
    const newSort = { type, criteria };
    setCurrentSort(newSort);
    // El useEffect de currentSort se encargará de persistir y reordenar
  };

  // 🔥 Función de desarrollo para limpiar localStorage
  const clearLocalStorage = () => {
    localStorage.removeItem("alexaLifterData");
    localStorage.removeItem("currentSort");
    localStorage.removeItem("activeTab");
    localStorage.removeItem("readOnlyMode");
    showToast("LocalStorage limpiado - Recarga la página");
  };

  // Componente de tabla
  const CompetitorTable = ({ type }) => {
    const field = type === "snatch" ? "snatch" : "cleanJerk";
    const label = type === "snatch" ? "Arranque" : "Envión";
    const criteria = currentSort.type === type ? currentSort.criteria : "best";
    const sorted = getSortedCompetitors(type, criteria);

    if (sorted.length === 0) {
      return (
        <div className="text-center py-16 text-gray-500">
          <div className="text-6xl mb-4">🏋️</div>
          <h3 className="text-xl font-bold mb-2">No hay competidores</h3>
          <p>Agrega tu primer competidor arriba</p>
        </div>
      );
    }

    if (readOnlyMode) {
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-xl">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                <th className="p-4 text-center font-bold">#</th>
                <th className="p-4 text-left font-bold">Nombre</th>
                <th className="p-4 text-center font-bold">1°</th>
                <th className="p-4 text-center font-bold">2°</th>
                <th className="p-4 text-center font-bold">3°</th>
                <th className="p-4 text-center font-bold">Mejor</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((comp, index) => {
                const best = getBest(comp[field]);
                const bestIndex = comp[field].indexOf(best);

                return (
                  <tr
                    key={comp.id}
                    className="hover:bg-slate-50 transition-colors border-b border-gray-100"
                  >
                    <td className="p-3 text-center">
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-lg w-8 h-8 flex items-center justify-center mx-auto">
                        {index + 1}
                      </div>
                    </td>
                    <td className="p-3 font-semibold text-slate-700 max-w-48 truncate">
                      {comp.name}
                    </td>
                    <td
                      className={`p-3 text-center font-semibold ${
                        bestIndex === 0 && comp[field][0] > 0
                          ? "text-slate-600"
                          : "text-slate-600"
                      }`}
                    >
                      {Array.isArray(comp[field]) ? comp[field][0] || "-" : "-"}
                    </td>
                    <td
                      className={`p-3 text-center font-semibold ${
                        bestIndex === 1 && comp[field][1] > 0
                          ? "text-slate-600"
                          : "text-slate-600"
                      }`}
                    >
                      {comp[field][1] || "-"}
                    </td>
                    <td
                      className={`p-3 text-center font-semibold ${
                        bestIndex === 2 && comp[field][2] > 0
                          ? "text-slate-600"
                          : "text-slate-600"
                      }`}
                    >
                      {comp[field][2] || "-"}
                    </td>
                    <td className="p-3 text-center">
                      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg px-3 py-1">
                        {best || "-"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-xl">
          <thead>
            <tr className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
              <th className="p-4 text-center font-bold">#</th>
              <th className="p-4 text-left font-bold">Nombre</th>
              <th className="p-4 text-center font-bold">1°</th>
              <th className="p-4 text-center font-bold">2°</th>
              <th className="p-4 text-center font-bold">3°</th>
              <th className="p-4 text-center font-bold">Mejor</th>
              <th className="p-4 text-center font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((comp, index) => {
              const best = getBest(comp[field]);

              return (
                <tr
                  key={comp.id}
                  className="hover:bg-slate-50 transition-colors border-b border-gray-100"
                >
                  <td className="p-3 text-center">
                    <div className="bg-orange-500 text-white font-bold rounded-lg w-8 h-8 flex items-center justify-center mx-auto">
                      {index + 1}
                    </div>
                  </td>
                  <td className="p-3 font-semibold text-slate-700">
                    <input
                      type="text"
                      defaultValue={comp.name}
                      onBlur={(e) =>
                        updateCompetitor(comp.id, "name", e.target.value)
                      }
                      className="bg-transparent outline-none font-semibold w-full"
                    />
                  </td>
                  {[0, 1, 2].map((i) => (
                    <td key={i} className="p-3 text-center">
                      <input
                        type="tel"
                        min="0"
                        max="500"
                        defaultValue={
                          Array.isArray(comp[field]) ? comp[field][i] || "" : ""
                        }
                        onBlur={(e) =>
                          updateAttempt(comp.id, field, i, e.target.value)
                        }
                        className="w-full text-center border font-semibold"
                      />
                    </td>
                  ))}
                  <td className="p-3 text-center">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg px-3 py-1">
                      {best || "-"}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => deleteCompetitor(comp.id)}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-sm"
                    >
                      🗑️ Eliminar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Componente de tabla total
  const TotalTable = () => {
    const sorted = getSortedCompetitors("total", "total");

    if (sorted.length === 0 && competitors.length > 0 && searchQuery) {
      return (
        <div className="text-center py-16 text-gray-500">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-bold mb-2">
            No se encontraron resultados
          </h3>
          <p>No hay competidores que coincidan con "{searchQuery}"</p>
          <button
            onClick={() => setSearchQuery("")}
            className="mt-4 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-all"
          >
            Limpiar búsqueda
          </button>
        </div>
      );
    }

    if (sorted.length === 0) {
      return (
        <div className="text-center py-16 text-gray-500">
          <div className="text-6xl mb-4">🏆</div>
          <h3 className="text-xl font-bold mb-2">No hay competidores</h3>
          <p>Agrega competidores para ver la clasificación</p>
        </div>
      );
    }

    if (readOnlyMode) {
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-xl">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                <th className="p-4 text-center font-bold">#</th>
                <th className="p-4 text-left font-bold">Nombre</th>
                <th className="p-4 text-center font-bold">Arranque</th>
                <th className="p-4 text-center font-bold">Envión</th>
                <th className="p-4 text-center font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((comp, index) => {
                const snatchBest = getBest(comp.snatch);
                const cleanJerkBest = getBest(comp.cleanJerk);
                const total = snatchBest + cleanJerkBest;

                return (
                  <tr
                    key={comp.id}
                    className="hover:bg-slate-50 transition-colors border-b border-gray-100"
                  >
                    <td className="p-3 text-center">
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-lg w-8 h-8 flex items-center justify-center mx-auto">
                        {index + 1}
                      </div>
                    </td>
                    <td className="p-3 font-semibold text-slate-700 max-w-48 truncate">
                      {comp.name}
                    </td>
                    <td className="p-3 text-center font-semibold text-slate-600">
                      {snatchBest || "-"}
                    </td>
                    <td className="p-3 text-center font-semibold text-slate-600">
                      {cleanJerkBest || "-"}
                    </td>
                    <td className="p-3 text-center">
                      <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white font-bold rounded-lg px-3 py-1">
                        {total || "-"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {sorted.map((comp, index) => {
          const snatchBest = getBest(comp.snatch);
          const cleanJerkBest = getBest(comp.cleanJerk);
          const total = snatchBest + cleanJerkBest;

          return (
            <div
              key={comp.id}
              className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all relative border border-gray-100"
            >
              {total > 0 && (
                <div className="absolute -top-2 right-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
                  {index + 1}
                </div>
              )}

              <div className="flex justify-between items-center mb-3">
                <div className="flex-1">
                  <h3
                    className="text-lg font-bold text-slate-700"
                    contentEditable={!readOnlyMode}
                    suppressContentEditableWarning={true}
                    onBlur={(e) =>
                      updateCompetitor(comp.id, "name", e.target.textContent)
                    }
                  >
                    {comp.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    <span
                      contentEditable={!readOnlyMode}
                      suppressContentEditableWarning={true}
                      onBlur={(e) =>
                        updateCompetitor(
                          comp.id,
                          "weight",
                          e.target.textContent
                        )
                      }
                    >
                      {comp.weight}
                    </span>{" "}
                    kg
                  </p>
                </div>
                <button
                  onClick={() => deleteCompetitor(comp.id)}
                  className="bg-red-500 hover:bg-red-600 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 text-sm"
                >
                  🗑️
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-lg text-center font-bold text-sm">
                  🏋️ {snatchBest}
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-lg text-center font-bold text-sm">
                  💪 {cleanJerkBest}
                </div>
                <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-3 rounded-lg text-center font-bold text-sm">
                  🏆 {total}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-100 to-gray-200">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50 shadow-xl">
        <div className="max-w-6xl mx-auto px-5 py-5 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="bg-white/10 p-3 rounded-xl flex items-center justify-center">
            <img src="./logo-alexa.png" width={80} />
          </div>
          <div className="flex items-center gap-4">
            <div className="text-lg font-bold text-white font-medium">
              {readOnlyMode ? (
                <h1>
                  GESTIÓN DE COMPETENCIA{" "}
                  <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs ml-2">
                    SOLO LECTURA
                  </span>
                </h1>
              ) : (
                "GESTIÓN DE COMPETENCIA"
              )}
            </div>
          </div>
          <button
            onClick={toggleReadOnlyMode}
            className={`px-5 py-3 rounded-full font-semibold text-white transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 ${
              readOnlyMode
                ? "bg-gradient-to-r from-orange-500 to-orange-600"
                : "bg-gradient-to-r from-slate-600 to-slate-700"
            }`}
          >
            {readOnlyMode ? "Modo Edición" : "Modo Lectura"}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-5 md:p-8">
        {/* Pestañas - Ahora siempre visibles */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl p-2 mb-8 shadow-xl border border-gray-200">
          <div className="flex">
            {[
              { id: "snatch", label: "Arranque", icon: "🏋️" },
              { id: "clean-jerk", label: "Envión", icon: "💪" },
              { id: "total", label: "Total", icon: "" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => changeTab(tab.id)}
                className={`flex-1 py-4 px-6 rounded-xl font-bold transition-all ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-lg -translate-y-0.5"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contenido de pestañas */}
        {!readOnlyMode && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-slate-600 to-slate-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              ➕ Agregar Competidor
            </button>
          </div>
        )}
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">
                {activeTab === "snatch"
                  ? "🏋️"
                  : activeTab === "clean-jerk"
                  ? "💪"
                  : "🏆"}
              </span>
              <h2 className="text-xl font-bold">
                Tabla de{" "}
                {activeTab === "snatch"
                  ? "Arranque"
                  : activeTab === "clean-jerk"
                  ? "Envión"
                  : "Total"}
              </h2>
            </div>

            <div className="flex gap-2 flex-wrap">
              {activeTab !== "total" && (
                <>
                  <button
                    onClick={() => changeSorting(activeTab, "weight")}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      currentSort.type === activeTab && currentSort.criteria === "weight"
                        ? "bg-white/30 text-white"
                        : "bg-white/20 hover:bg-white/30"
                    }`}
                  >
                    ⚖️ Peso
                  </button>
                  <button
                    onClick={() => changeSorting(activeTab, "name")}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      currentSort.type === activeTab && currentSort.criteria === "name"
                        ? "bg-white/30 text-white"
                        : "bg-white/20 hover:bg-white/30"
                    }`}
                  >
                    🔤 Nombre
                  </button>
                  <button
                    onClick={() => changeSorting(activeTab, "best")}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      currentSort.type === activeTab && currentSort.criteria === "best"
                        ? "bg-white/30 text-white"
                        : "bg-white/20 hover:bg-white/30"
                    }`}
                  >
                    🏆 Mejor
                  </button>
                </>
              )}
              {activeTab === "total" && (
                <button
                  onClick={() => changeSorting("total", "total")}
                  className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg text-sm font-medium transition-all"
                >
                  🏆 Total
                </button>
              )}
            </div>
          </div>

          {/* Buscador */}
          {competitors.length > 0 && (
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <div className="relative max-w-md mx-auto">
                <input
                  type="text"
                  placeholder="Buscar competidor por nombre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl font-medium transition-all focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 bg-white"
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                  🔍
                </span>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchQuery && (
                <div className="text-center mt-2 text-sm text-gray-600">
                  {getSortedCompetitors(activeTab, currentSort.criteria).length}{" "}
                  resultado(s) encontrado(s)
                </div>
              )}
            </div>
          )}

          <div className="p-6">
            {activeTab === "snatch" && <CompetitorTable type="snatch" />}
            {activeTab === "clean-jerk" && (
              <CompetitorTable type="clean-jerk" />
            )}
            {activeTab === "total" && <TotalTable />}
          </div>
          <div className="text-center p-3">
            {!readOnlyMode && competitors.length > 0 && (
              <>
                <button
                  onClick={clearAll}
                  className="bg-gradient-to-r m-2 from-red-500 to-red-500 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm"
                >
                  Limpiar todo
                </button>
                <button
                  onClick={saveCurrentDataToAPI}
                  className="bg-gradient-to-r from-slate-600 to-slate-700 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm"
                >
                  💾 Guardar Cambios
                </button>
                {/* <button
                  onClick={clearLocalStorage}
                  className="bg-gradient-to-r m-2 from-yellow-500 to-yellow-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm"
                >
                  🧹 Limpiar Cache
                </button> */}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Agregar Competidor */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold text-slate-700 mb-6">
              👤 Nuevo Competidor
            </h2>

            <form
              onSubmit={(e) => {
                handleSubmit(e);
                setIsModalOpen(false); // cerrar modal después de agregar
              }}
            >
              <div className="grid md:grid-cols-2 gap-5 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre del Competidor
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full p-4 border-2 border-gray-200 rounded-xl font-medium transition-all focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                    placeholder="Ingresa el nombre completo"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Peso Corporal (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) =>
                      setFormData({ ...formData, weight: e.target.value })
                    }
                    className="w-full p-4 border-2 border-gray-200 rounded-xl font-medium transition-all focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                    placeholder="Ingresa el peso"
                    min="1"
                    step="0.1"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-slate-700 to-slate-800 text-white py-4 px-6 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Agregar
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div
          className={`fixed bottom-8 right-8 px-6 py-4 rounded-xl font-semibold shadow-lg z-50 animate-pulse ${
            toast.type === "error"
              ? "bg-red-500 text-white"
              : "bg-green-500 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AlexaLifterPro;